import React, { useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { Add } from '@mui/icons-material'
import { TableContainer } from '@mui/material'

import SectionTitle from '../../../components/form/SectionTitle'
import Datatable, { DatatableHeaderProps } from '../../../components/layout/Datatable'
import { FormDialog } from '../../../components/layout/FormDialog'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { RedDeleteIcon } from '../../../components/form/styled'
import { EditionModeContextProvider } from '../../../utils/providers/editionModeContext'
import { GetCompanyDetailsQuery } from '../../../__generated__/graphql'
import { WebsiteCreatePositionDraft } from './websiteCreateDrafts'
import { WebsiteCreatePositionModal } from './WebsiteCreatePositionModal'

type PositionListItem = {
  id: string
  name: string
  channelType: string
  network: string
}

interface WebsiteCreatePositionsSectionProps {
  company?: GetCompanyDetailsQuery['company']
}

export const WebsiteCreatePositionsSection: React.FC<WebsiteCreatePositionsSectionProps> = ({ company }) => {
  const t = useAppTranslations()
  const { values, setFieldValue } = useFormikContext<{
    positions: WebsiteCreatePositionDraft[]
    publisherId?: number
    iabCategory?: string
  }>()
  const [openCreateModal, setOpenCreateModal] = useState(false)

  const positions = values.positions ?? []
  const publisherCompanyId = company?.id ?? values.publisherId

  const tableData: PositionListItem[] = useMemo(
    () =>
      positions.map((position) => ({
        id: position.draftId,
        name: position.name,
        channelType: position.channelType,
        network: position.network,
      })),
    [positions]
  )

  const tableHeaders: DatatableHeaderProps<keyof PositionListItem>[] = useMemo(
    () => [{ name: 'name' }, { name: 'channelType' }, { name: 'network' }],
    []
  )

  const datatableButtons = useMemo(
    () => [
      {
        type: () => <RedDeleteIcon />,
        action: (item: PositionListItem) => {
          setFieldValue(
            'positions',
            positions.filter((position) => position.draftId !== item.id)
          )
        },
        label: t('commons.actions.delete'),
      },
    ],
    [positions, t]
  )

  const actionButtons = useMemo(() => {
    if (!publisherCompanyId) return []

    return [
      {
        startIcon: <Add />,
        label: 'commons.actions.add',
        testingId: 'addWebsiteCreatePosition',
        onClick: () => setOpenCreateModal(true),
      },
    ]
  }, [publisherCompanyId])

  return (
    <EditionModeContextProvider>
      <SectionTitle
        withFormik={false}
        title="website.details.positions"
        helperText={t('website.positionsHelper')}
        actionButtons={actionButtons}
      />
      <TableContainer>
        {tableData.length > 0 ? (
          <Datatable
            headers={tableHeaders}
            data={tableData}
            actionsButtons={datatableButtons}
            translateKey="commons."
          />
        ) : null}
      </TableContainer>

      {publisherCompanyId ? (
        <FormDialog title={t('position.create.title')} open={openCreateModal} setOpen={setOpenCreateModal}>
          <WebsiteCreatePositionModal
            setOpen={setOpenCreateModal}
            publisherCompanyId={publisherCompanyId}
            defaultIabCategory={values.iabCategory}
            onAdd={(position) => setFieldValue('positions', [...positions, position])}
          />
        </FormDialog>
      ) : null}
    </EditionModeContextProvider>
  )
}
